import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { lookupFailedMessage } from '../../components/ui/resultWording.js';
import { useFetch } from '../../hooks/useFetch.js';

/**
 * 058: the filter panel.
 *
 * Holds its own copy of the filter set and only hands it back on Apply. That is not a
 * stylistic choice: the search screen refetches whenever its committed filters change, so
 * letting each control out immediately would fire one request per keystroke on the price
 * fields and one per tick on the features (FR-014).
 */

/** Everything the shopper can narrow by. Strings, because these come from form controls. */
export interface FilterState {
  brand_id: string;
  category_id: string;
  feature_ids: string[];
  /** Major units (RM), as typed. Converted to minor units when the query is built. */
  min_price_hour: string;
  max_price_hour: string;
  transmission: string;
  fuel_type: string;
  /** 363 (FR-014): `'4'` for "★ 4 and up", `''` for any. */
  min_rating: string;
}

export const EMPTY_FILTERS: FilterState = {
  brand_id: '',
  category_id: '',
  feature_ids: [],
  min_price_hour: '',
  max_price_hour: '',
  transmission: '',
  fuel_type: '',
  min_rating: '',
};

/** How many narrowings are in effect — the number the Filters button shows (FR-013). */
export function activeFilterCount(f: FilterState): number {
  return (
    (f.brand_id ? 1 : 0) +
    (f.category_id ? 1 : 0) +
    (f.feature_ids.length > 0 ? 1 : 0) +
    (f.min_price_hour || f.max_price_hour ? 1 : 0) +
    (f.transmission ? 1 : 0) +
    (f.fuel_type ? 1 : 0) +
    (f.min_rating ? 1 : 0)
  );
}

/**
 * The catalogue payloads key on `_id`, not `id`, and categories/features are raw documents
 * (they still carry `__v`). Reading `id` here would render every option with an undefined
 * value — the seam behind the session-78 and 044 defects.
 */
interface TaxonomyOption {
  _id: string;
  name: string;
}

interface SearchFiltersProps {
  open: boolean;
  /** The filters currently in effect; the panel opens showing these (FR-016). */
  value: FilterState;
  onApply: (next: FilterState) => void;
  onDismiss: () => void;
}

export function SearchFilters({ open, value, onApply, onDismiss }: SearchFiltersProps) {
  const [draft, setDraft] = useState<FilterState>(value);

  // Re-sync whenever the panel is opened, so it always reflects what is in effect rather
  // than whatever the shopper abandoned last time (FR-016, FR-017).
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const brands = useFetch<TaxonomyOption[]>(open ? '/car-brands?status=active' : null);
  const categories = useFetch<TaxonomyOption[]>(open ? '/car-categories?status=active' : null);
  const features = useFetch<TaxonomyOption[]>(open ? '/car-features?status=active' : null);

  /**
   * 064: a lookup that could not be read states itself where it lives. An empty dropdown reads
   * as "this platform has no brands" — the same defect as the empty fleet, one control down.
   * These are lookups, not this panel's subject, so nothing here blanks the panel (FR-010).
   */
  const lookupFailed = (s: string) => s !== 'ok' && s !== 'loading' && s !== 'idle';

  const set = <K extends keyof FilterState>(key: K, v: FilterState[K]) =>
    setDraft((d) => ({ ...d, [key]: v }));

  const toggleFeature = (id: string) =>
    setDraft((d) => ({
      ...d,
      feature_ids: d.feature_ids.includes(id)
        ? d.feature_ids.filter((f) => f !== id)
        : [...d.feature_ids, id],
    }));

  return (
    <Sheet
      open={open}
      title="Filters"
      onDismiss={onDismiss}
      footer={
        <>
          <Button variant="ghost" onClick={() => onApply(EMPTY_FILTERS)}>
            Clear all
          </Button>
          <Button className="flex-1" onClick={() => onApply(draft)}>
            Apply
          </Button>
        </>
      }
    >
      {/* 363 (design 2b): Any or 4 and up. 4.5 would empty a small campus's list. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm text-gray-400 mb-1">Rating</legend>
        <div className="flex flex-wrap gap-2">
          {[
            { value: '', text: 'Any', label: 'Any rating' },
            { value: '4', text: '★ 4 and up', label: '4 stars and up' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              aria-label={o.label}
              aria-pressed={draft.min_rating === o.value}
              onClick={() => set('min_rating', o.value)}
              className={`h-9 rounded-full border px-3.5 text-sm font-semibold ${
                draft.min_rating === o.value
                  ? 'border-primary bg-primary text-on-primary'
                  : 'border-border text-text'
              }`}
            >
              {o.text}
            </button>
          ))}
        </div>
        {draft.min_rating && (
          <p className="text-xs text-gray-400">Cars with no reviews yet are not shown.</p>
        )}
      </fieldset>

      {lookupFailed(brands.status) ? (
        <p data-testid="filter-brand-error" className="text-sm text-error">
          {lookupFailedMessage('Brands')}
        </p>
      ) : (
        <Select
          id="filter-brand"
          label="Brand"
          placeholder="Any brand"
          value={draft.brand_id}
          options={brands.data ?? []}
          onChange={(v) => set('brand_id', v)}
        />
      )}

      {lookupFailed(categories.status) ? (
        <p data-testid="filter-category-error" className="text-sm text-error">
          {lookupFailedMessage('Categories')}
        </p>
      ) : (
        <Select
          id="filter-category"
          label="Category"
          placeholder="Any category"
          value={draft.category_id}
          options={categories.data ?? []}
          onChange={(v) => set('category_id', v)}
        />
      )}

      {lookupFailed(features.status) && (
        <p data-testid="filter-features-error" className="text-sm text-error">
          {lookupFailedMessage('Features')}
        </p>
      )}

      {(features.data ?? []).length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm text-gray-400 mb-1">Features</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {(features.data ?? []).map((f) => (
              <label key={f._id} className="flex items-center gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  checked={draft.feature_ids.includes(f._id)}
                  onChange={() => toggleFeature(f._id)}
                />
                {f.name}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="flex flex-col gap-1">
        {/* Shown and typed in major units — a shopper thinks in RM, not sen. The API takes
            minor units, so the conversion happens at this boundary and nowhere else. */}
        <span className="text-sm text-gray-400">Price per hour (RM)</span>
        <div className="flex items-center gap-2">
          <input
            id="filter-price-min"
            aria-label="Minimum price per hour"
            type="number"
            min={0}
            step="0.01"
            placeholder="Min"
            className="w-full bg-surface-mid text-text rounded-lg px-3 py-2 text-sm"
            value={draft.min_price_hour}
            onInput={(e) => set('min_price_hour', (e.target as HTMLInputElement).value)}
          />
          <span className="text-gray-500">–</span>
          <input
            id="filter-price-max"
            aria-label="Maximum price per hour"
            type="number"
            min={0}
            step="0.01"
            placeholder="Max"
            className="w-full bg-surface-mid text-text rounded-lg px-3 py-2 text-sm"
            value={draft.max_price_hour}
            onInput={(e) => set('max_price_hour', (e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      <Select
        id="filter-transmission"
        label="Transmission"
        placeholder="All transmission"
        value={draft.transmission}
        options={[
          { _id: 'auto', name: 'Automatic' },
          { _id: 'manual', name: 'Manual' },
        ]}
        onChange={(v) => set('transmission', v)}
      />

      <Select
        id="filter-fuel"
        label="Fuel"
        placeholder="All fuel"
        value={draft.fuel_type}
        options={[
          { _id: 'petrol', name: 'Petrol' },
          { _id: 'diesel', name: 'Diesel' },
          { _id: 'electric', name: 'Electric' },
          { _id: 'hybrid', name: 'Hybrid' },
        ]}
        onChange={(v) => set('fuel_type', v)}
      />
    </Sheet>
  );
}

function Select({
  id,
  label,
  placeholder,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  options: TaxonomyOption[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-gray-400" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="bg-surface-mid text-text rounded-lg px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o._id} value={o._id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}
